import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Quote, Heart } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { formatDate, calculateAge } from '@/utils/dateUtils';

export default function CutePhrases() {
  const navigate = useNavigate();
  const { memories, children, tags, toggleFavorite } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // 获取童言趣语（标记为 isCutePhrase 或包含"童言趣语"标签）
  const cutePhraseTag = tags.find(t => t.name === '童言趣语');
  
  const cutePhrases = useMemo(() => {
    return memories.filter(m => {
      const isCutePhrase = m.isCutePhrase || 
        (cutePhraseTag && m.tags.includes(cutePhraseTag.id));
      return isCutePhrase;
    });
  }, [memories, cutePhraseTag]);

  // 过滤和搜索
  const filteredPhrases = useMemo(() => {
    let result = cutePhrases;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.content.toLowerCase().includes(query)
      );
    }
    
    if (selectedTag) {
      result = result.filter(m => m.tags.includes(selectedTag));
    }
    
    return result.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [cutePhrases, searchQuery, selectedTag]);

  // 获取所有用于童言趣语的标签
  const usedTags = useMemo(() => {
    const tagIds = new Set(cutePhrases.flatMap(m => m.tags));
    return tags.filter(t => tagIds.has(t.id));
  }, [cutePhrases, tags]);

  return (
    <div className="px-4 py-4">
      {/* 搜索栏 */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="搜索童言趣语..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-gray-100 focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
        />
      </div>

      {/* 标签过滤 */}
      {usedTags.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedTag(null)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm transition-colors ${
              !selectedTag 
                ? 'bg-primary-500 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            全部
          </button>
          {usedTags.map(tag => (
            <button
              key={tag.id}
              onClick={() => setSelectedTag(selectedTag === tag.id ? null : tag.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm transition-colors ${
                selectedTag === tag.id 
                  ? 'text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              style={selectedTag === tag.id ? { backgroundColor: tag.color } : undefined}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* 统计 */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
        <Quote className="w-4 h-4" />
        <span>共 {filteredPhrases.length} 条童言趣语</span>
      </div>

      {/* 列表 */}
      {filteredPhrases.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-baby-pink/20 flex items-center justify-center">
            <Quote className="w-10 h-10 text-baby-pink" />
          </div>
          <p>{searchQuery ? '没有找到匹配的童言趣语' : '还没有记录童言趣语'}</p>
          <p className="text-sm mt-1">在添加记录时标记为"童言趣语"即可在此查看</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPhrases.map((phrase, index) => {
            const child = children.find(c => c.id === phrase.childId);
            const phraseTags = tags.filter(t => phrase.tags.includes(t.id));
            
            return (
              <div
                key={phrase.id}
                onClick={() => navigate(`/memory/${phrase.id}`)}
                className="bg-white rounded-2xl p-4 card-shadow animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* 引用样式 */}
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 w-6 h-6 text-baby-pink/30" />
                  <p className="text-gray-800 text-base leading-relaxed pl-4 py-2">
                    {phrase.content}
                  </p>
                </div>

                {/* 底部信息 */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: child?.color || '#ccc' }}
                    >
                      {child?.name.charAt(0) || '?'}
                    </div>
                    <span className="text-sm text-gray-600">{child?.name}</span>
                    {child?.birthDate && child.type !== 'family' && (
                      <span className="text-xs text-gray-400">
                        {calculateAge(child.birthDate, phrase.createdAt)}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {formatDate(phrase.createdAt)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {phraseTags.slice(0, 2).map(tag => (
                      <span 
                        key={tag.id}
                        className="px-2 py-0.5 rounded-full text-xs"
                        style={{ 
                          backgroundColor: `${tag.color}20`,
                          color: tag.color 
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(phrase.id);
                      }}
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <Heart 
                        className={`w-4 h-4 ${
                          phrase.isFavorite 
                            ? 'fill-red-500 text-red-500' 
                            : 'text-gray-300'
                        }`} 
                      />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="h-20" />
    </div>
  );
}
